import { useDialogs } from '../ui'
/**
 * The editor's Translations panel: the template's default language, its locales, and per locale a
 * table of every source string against its translation with a status (missing / draft / accepted),
 * a filter, inline editing, Translate with AI, Accept all drafts, and CSV/JSON import and export.
 *
 * Translations are part of the document (CanonicalDocument.localization), so edits here change the
 * editor's own state and are saved — versioned, then promoted — with the layout by Save draft. The
 * list of strings comes from the server (translations/inspect) so its keys always match the ones the
 * renderer uses.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import type { CanonicalDocument, DocumentLocalization, TranslationOverride } from '../../cdm/types'
import { TranslationStatus } from '../../cdm/types'
import {
  COMMON_LOCALES, acceptAllDrafts, addLocale, findLocale, findOverride, isWellFormed, normalizeLocaleTag,
  parseImport, putEntry, putOverride, removeLocale, setDefaultLanguage, toCsv, toJson, type ExportRow,
} from '../../cdm/localization'
import { translationsApiService, type AiDraftResult, type TranslationRow } from '../../services/translationsApiService'
import { usageApiService } from '../../services/usageApiService'
import { ApiError } from '../../services/BaseApiService'
import { formatCents } from '../../utils/billingStanding'
import AiTranslateDialog from './AiTranslateDialog'

type Filter = 'all' | 'missing' | 'draft' | 'accepted' | 'invalid'

interface Props {
  /** The editor's current document, built on demand (docToCdm). */
  getDocument: () => CanonicalDocument | null
  localization: DocumentLocalization | null | undefined
  onChange: (next: DocumentLocalization) => void
  /** Bumps whenever the document text changes, so the string list follows edits. */
  refreshKey: number
  canEdit: boolean
  onClose?: () => void
  /** The template being edited, so an AI translation charge names it on the invoice. */
  templateId?: string
}

interface Row extends TranslationRow {
  /** Status from the editor's own state, which may be ahead of the last inspect call. */
  localStatus: 'missing' | 'draft' | 'accepted' | 'invalid'
  localText: string
  inherited: string | null
  /** This locale's per-occurrence translations of the string, by context. */
  overrides: Map<string, TranslationOverride>
}

function parentsOf(locale: string): string[] {
  const parts = locale.split('-')
  const out: string[] = []
  for (let i = parts.length - 1; i >= 1; i--) out.push(parts.slice(0, i).join('-'))
  return out
}

function download(name: string, body: string, type: string) {
  const url = URL.createObjectURL(new Blob([body], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export default function TranslationsPanel({ getDocument, localization, onChange, refreshKey, canEdit, onClose, templateId }: Props) {
  const dialogs = useDialogs()
  const [strings, setStrings] = useState<TranslationRow[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(localization?.locales[0]?.locale ?? null)
  const [newLocale, setNewLocale] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const locales = localization?.locales ?? []
  // Translate with AI is an Ultimate feature. Unknown (null) until the plan is read; the server gates it either way.
  const [aiAllowed, setAiAllowed] = useState<boolean | null>(null)
  const [aiOpen, setAiOpen] = useState(false)

  useEffect(() => {
    if (!canEdit) return
    let live = true
    usageApiService.getSummary()
      .then(s => { if (live) setAiAllowed(s.standing.planCode === 'ultimate') })
      .catch(() => { if (live) setAiAllowed(null) })
    return () => { live = false }
  }, [canEdit])

  // The string list: re-fetched when the document's text changes (debounced by the caller's key).
  useEffect(() => {
    const doc = getDocument()
    if (!doc) return
    const ctrl = new AbortController()
    const t = window.setTimeout(() => {
      translationsApiService.inspect(doc, undefined, ctrl.signal)
        .then(r => { setStrings(r.strings); setLoadError(null) })
        .catch(err => { if (!ctrl.signal.aborted) setLoadError(err instanceof ApiError ? err.message : 'Could not list the strings.') })
    }, 300)
    return () => { window.clearTimeout(t); ctrl.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])

  useEffect(() => {
    if (selected && !findLocale(localization, selected)) setSelected(locales[0]?.locale ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localization])

  const rows: Row[] = useMemo(() => {
    if (!strings || !selected) return []
    const table = findLocale(localization, selected)
    const parentTables = parentsOf(selected).map(p => findLocale(localization, p)).filter(Boolean)
    return strings.map(s => {
      const entry = table?.entries.find(e => e.key === s.key && e.text)
      const inherited = entry ? null : parentTables.find(t => t!.entries.some(e => e.key === s.key && e.text))?.locale ?? null
      const localStatus: Row['localStatus'] = !entry ? 'missing'
        : !isWellFormed(s.source, entry.text) ? 'invalid'
        : entry.status === TranslationStatus.Draft ? 'draft' : 'accepted'
      const overrides = new Map<string, TranslationOverride>()
      for (const c of s.contexts ?? []) {
        const o = findOverride(table, s.key, c.context)
        if (o) overrides.set(c.context, o)
      }
      return { ...s, localStatus, localText: entry?.text ?? '', inherited, overrides }
    })
  }, [strings, localization, selected])

  // Per-occurrence translations whose occurrence is gone (heading reworded, copy deleted): not applied.
  const stale = useMemo(() => {
    if (!strings || !selected) return []
    const live = new Set(strings.flatMap(s => (s.contexts ?? []).map(c => `${s.key}\u0000${c.context}`)))
    return (findLocale(localization, selected)?.overrides ?? []).filter(o => !live.has(`${o.key}\u0000${o.context}`))
  }, [strings, localization, selected])

  const counts = useMemo(() => ({
    missing: rows.filter(r => r.localStatus === 'missing').length,
    draft: rows.filter(r => r.localStatus === 'draft').length,
    overrideDrafts: rows.reduce((n, r) => n + [...r.overrides.values()].filter(o => o.status === TranslationStatus.Draft).length, 0),
    accepted: rows.filter(r => r.localStatus === 'accepted').length,
    invalid: rows.filter(r => r.localStatus === 'invalid').length,
  }), [rows])

  const visible = rows.filter(r =>
    (filter === 'all' || r.localStatus === filter) &&
    (!search || r.source.toLowerCase().includes(search.toLowerCase()) || r.localText.toLowerCase().includes(search.toLowerCase())))

  function handleAddLocale() {
    const tag = normalizeLocaleTag(newLocale)
    if (!tag) { setNotice({ kind: 'error', text: `"${newLocale}" is not a language tag (e.g. fr-CA).` }); return }
    onChange(addLocale(localization, tag))
    setSelected(tag)
    setNewLocale('')
    setNotice(null)
  }

  async function handleRemoveLocale(tag: string) {
    if (!(await dialogs.confirm({ title: `Remove ${tag}?`, message: 'All of its translations are removed from this draft.', confirmLabel: 'Remove', danger: true }))) return
    onChange(removeLocale(localization, tag))
  }

  function commit(row: Row, text: string) {
    if (!selected || text === row.localText) return
    onChange(putEntry(localization, selected, row.key, row.source, text, TranslationStatus.Accepted))
  }

  function commitOverride(row: Row, context: string, text: string, status: TranslationStatus = TranslationStatus.Accepted) {
    if (!selected) return
    const current = row.overrides.get(context)
    if (current ? current.text === text && current.status === status : !text) return
    onChange(putOverride(localization, selected, row.key, context, row.source, text, status))
  }

  function removeStale() {
    if (!selected) return
    let next = localization
    for (const o of stale) next = putOverride(next, selected, o.key, o.context, o.source, '')
    if (next) onChange(next)
  }

  /**
   * Merges a confirmed Translate with AI run into the editor's document: per locale (adding locales that were
   * new), shared drafts and per-occurrence drafts, never over an accepted translation.
   */
  function applyAiResult(result: AiDraftResult) {
    const sources = new Map((strings ?? []).map(s => [s.key, s.source]))
    let next = localization
    const parts: string[] = []
    for (const r of result.results) {
      if (r.failed) { parts.push(`${r.locale}: failed, not charged`); continue }
      const table = findLocale(next, r.locale)
      const accepted = (key: string) => table?.entries.some(e => e.key === key && e.text && e.status === TranslationStatus.Accepted)
      let shared = 0
      for (const d of r.drafts) {
        const source = sources.get(d.key)
        if (!source || accepted(d.key)) continue
        next = putEntry(next, r.locale, d.key, source, d.text, TranslationStatus.Draft)
        shared++
      }
      let occurrence = 0
      for (const o of r.overrideDrafts) {
        const source = sources.get(o.key)
        if (!source || findOverride(table, o.key, o.context)?.status === TranslationStatus.Accepted) continue
        next = putOverride(next, r.locale, o.key, o.context, source, o.text, TranslationStatus.Draft)
        occurrence++
      }
      const discarded = r.discarded.length + r.overridesDiscarded.length
      parts.push(`${r.locale}: ${shared} draft(s), ${occurrence} occurrence draft(s), ${discarded} discarded` +
        (r.overridesSkipped ? `, ${r.overridesSkipped} repeated string(s) not checked per occurrence` : ''))
    }
    if (next) onChange(next)
    if (!selected && result.results[0]) setSelected(result.results[0].locale)
    setNotice({
      kind: 'ok',
      text: `${parts.join('; ')}. Charged ${formatCents(result.chargedCents)}` +
        (result.chargedCents ? ', added to your next invoice' : '') + '. Review the drafts, then accept.',
    })
  }

  function exportRows(): ExportRow[] {
    return rows.flatMap(r => [
      { key: r.key, source: r.source, text: r.localText || null, status: r.localStatus === 'invalid' ? 'accepted' : r.localStatus },
      ...[...r.overrides.values()].map(o => ({
        key: r.key, context: o.context, source: r.source, text: o.text, status: o.status === TranslationStatus.Draft ? 'draft' : 'accepted',
      })),
    ])
  }

  async function handleImport(file: File) {
    if (!selected) return
    const format = file.name.toLowerCase().endsWith('.json') ? 'json' : 'csv'
    try {
      const units = parseImport(await file.text(), format)
      let next = localization
      let stored = 0
      const rejected: string[] = []
      for (const u of units) {
        const row = rows.find(r => r.key === u.key)
        if (!row || !isWellFormed(row.source, u.text) || (u.context && !row.contexts?.some(c => c.context === u.context))) { rejected.push(u.key); continue }
        const status = u.draft ? TranslationStatus.Draft : TranslationStatus.Accepted
        next = u.context
          ? putOverride(next, selected, u.key, u.context, row.source, u.text, status)
          : putEntry(next, selected, u.key, row.source, u.text, status)
        stored++
      }
      if (next && stored) onChange(next)
      setNotice({ kind: rejected.length ? 'error' : 'ok', text: `Imported ${stored} translation(s)` + (rejected.length ? `; ${rejected.length} skipped (unknown key or occurrence, or a {{variable}} or tag does not match).` : '.') })
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof Error ? err.message : 'Import failed.' })
    }
  }

  const languageOptions = COMMON_LOCALES.some(l => l.tag === localization?.defaultLanguage) || !localization?.defaultLanguage
    ? COMMON_LOCALES
    : [{ tag: localization.defaultLanguage, label: localization.defaultLanguage }, ...COMMON_LOCALES]

  const small: CSSProperties = { padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, color: colors.textSecondary, fontSize: fontSize.xs, cursor: 'pointer' }

  return (
    <>
    <aside aria-label="Translations" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: colors.surface, fontSize: fontSize.sm }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: `${spacing.sm} ${spacing[3]}`, borderBottom: `1px solid ${colors.border}` }}>
        <span style={{ fontWeight: 600, color: colors.text, flex: 1 }}>Translations</span>
        {onClose && <button type="button" aria-label="Close translations panel" onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: colors.textSecondary, fontSize: fontSize.base }}>×</button>}
      </div>

      <div style={{ padding: `${spacing.sm} ${spacing[3]}`, borderBottom: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: colors.textSecondary, fontSize: fontSize.xs }}>
          Default language (the language this document is written in)
          <select
            aria-label="Default language"
            value={localization?.defaultLanguage ?? ''}
            disabled={!canEdit}
            onChange={e => onChange(setDefaultLanguage(localization, e.target.value || null))}
            style={{ padding: '4px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm }}
          >
            <option value="">Not set (invariant formatting)</option>
            {languageOptions.map(l => <option key={l.tag} value={l.tag}>{l.label} — {l.tag}</option>)}
          </select>
        </label>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="tablist" aria-label="Locales">
          {locales.length === 0 && <span style={{ color: colors.textMuted, fontSize: fontSize.xs }}>No locales yet. Add one to start translating.</span>}
          {locales.map(l => (
            <span key={l.locale} style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${selected === l.locale ? colors.primary : colors.border}`, borderRadius: 999, background: selected === l.locale ? colors.primaryLight : 'none' }}>
              <button type="button" role="tab" aria-selected={selected === l.locale} onClick={() => setSelected(l.locale)}
                style={{ border: 'none', background: 'none', padding: '2px 8px', cursor: 'pointer', color: selected === l.locale ? colors.primary : colors.textSecondary, fontWeight: 600, fontSize: fontSize.xs }}>
                {l.locale}
              </button>
              {canEdit && (
                <button type="button" aria-label={`Remove ${l.locale}`} onClick={() => handleRemoveLocale(l.locale)}
                  style={{ border: 'none', background: 'none', padding: '2px 6px 2px 0', cursor: 'pointer', color: colors.textMuted, fontSize: fontSize.xs }}>×</button>
              )}
            </span>
          ))}
        </div>

        {canEdit && (
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              aria-label="New locale"
              list="dm-locale-options"
              value={newLocale}
              onChange={e => setNewLocale(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddLocale() }}
              placeholder="Add locale, e.g. fr-CA"
              style={{ flex: 1, minWidth: 0, padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm }}
            />
            <datalist id="dm-locale-options">
              {COMMON_LOCALES.map(l => <option key={l.tag} value={l.tag}>{l.label}</option>)}
            </datalist>
            <button type="button" onClick={handleAddLocale} disabled={!newLocale.trim()} style={small}>Add</button>
          </div>
        )}
      </div>

      {notice && (
        <div role="status" style={{ padding: `${spacing.xs} ${spacing[3]}`, fontSize: fontSize.xs, background: notice.kind === 'ok' ? colors.successLight : colors.errorLight, color: notice.kind === 'ok' ? colors.success : colors.error }}>
          {notice.text}
        </div>
      )}

      {canEdit && aiAllowed === false && (
        <div data-testid="ai-upsell" style={{ padding: `${spacing.xs} ${spacing[3]}`, fontSize: fontSize.xs, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
          Translate with AI is available on the Ultimate plan (paid per use). <a href="/app/billing" style={{ color: colors.primary, fontWeight: 600 }}>View plans</a>
        </div>
      )}
      {canEdit && !selected && (
        <div style={{ padding: `${spacing.sm} ${spacing[3]}`, borderBottom: `1px solid ${colors.border}` }}>
          <button type="button" onClick={() => { setNotice(null); setAiOpen(true) }} disabled={busy || aiAllowed === false || !strings} style={small}>Translate with AI</button>
        </div>
      )}
      {aiOpen && (
        <AiTranslateDialog
          getDocument={getDocument}
          templateId={templateId}
          preselect={selected}
          onCancel={() => setAiOpen(false)}
          onBusy={setBusy}
          onDone={result => { setAiOpen(false); applyAiResult(result) }}
          onError={(text, upgrade) => { setAiOpen(false); if (upgrade) setAiAllowed(false); setNotice({ kind: 'error', text }) }}
        />
      )}
      {selected && (
        <div style={{ padding: `${spacing.sm} ${spacing[3]}`, borderBottom: `1px solid ${colors.border}`, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <select aria-label="Filter strings" value={filter} onChange={e => setFilter(e.target.value as Filter)} style={{ ...small, cursor: 'default' }}>
            <option value="all">All ({rows.length})</option>
            <option value="missing">Missing ({counts.missing})</option>
            <option value="draft">Draft ({counts.draft})</option>
            <option value="accepted">Accepted ({counts.accepted})</option>
            {counts.invalid > 0 && <option value="invalid">Invalid ({counts.invalid})</option>}
          </select>
          <input aria-label="Search strings" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" style={{ ...small, cursor: 'text', flex: '1 1 80px', minWidth: 0 }} />
          {canEdit && (
            <>
              <button type="button" onClick={() => { setNotice(null); setAiOpen(true) }} disabled={busy || aiAllowed === false || !strings} style={small}
                title={aiAllowed === false ? 'Translate with AI is on the Ultimate plan' : 'Choose languages, see the price, then translate; drafts render with a warning until accepted'}>
                {busy ? 'Translating…' : 'Translate with AI'}
              </button>
              <button type="button" onClick={() => onChange(acceptAllDrafts(localization, selected))} disabled={counts.draft + counts.overrideDrafts === 0} style={small}>
                Accept all drafts{counts.draft + counts.overrideDrafts ? ` (${counts.draft + counts.overrideDrafts})` : ''}
              </button>
              <button type="button" onClick={() => fileRef.current?.click()} style={small}>Import</button>
              <input ref={fileRef} type="file" accept=".csv,.json,text/csv,application/json" aria-label="Import translations file" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) void handleImport(f); e.target.value = '' }} />
            </>
          )}
          <button type="button" onClick={() => download(`translations-${selected}.csv`, '﻿' + toCsv(exportRows()), 'text/csv')} style={small}>Export CSV</button>
          <button type="button" onClick={() => download(`translations-${selected}.json`, toJson(exportRows(), localization?.defaultLanguage ?? null, selected), 'application/json')} style={small}>Export JSON</button>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: `${spacing.sm} ${spacing[3]}` }}>
        {loadError && <div style={{ color: colors.error, fontSize: fontSize.xs }}>{loadError}</div>}
        {stale.length > 0 && (
          <div role="alert" style={{ marginBottom: spacing.sm, padding: spacing.xs, fontSize: fontSize.xs, borderRadius: radius.sm, background: colors.warningLight, color: colors.warning }}>
            {stale.length} per-occurrence translation(s) no longer match a place in the document (a heading was reworded or the
            text was removed); the shared translation is used there.
            {canEdit && <button type="button" onClick={removeStale} style={{ ...small, marginLeft: 6, padding: '1px 6px' }}>Remove them</button>}
          </div>
        )}
        {!selected ? (
          <div style={{ color: colors.textMuted, fontSize: fontSize.xs, lineHeight: 1.5 }}>
            Static text is translatable; <code>{'{{variables}}'}</code> stay as they are and are filled in at render time.
            Choose the render language with the request's <code>locale</code> field or a <code>locale</code> key in the payload.
          </div>
        ) : strings === null ? (
          <div style={{ color: colors.textMuted }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ color: colors.textMuted }}>No strings match.</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {visible.map(r => <StringRow key={`${selected}:${r.key}`} row={r} canEdit={canEdit} onCommit={text => commit(r, text)}
              onAccept={() => selected && onChange(putEntry(localization, selected, r.key, r.source, r.localText, TranslationStatus.Accepted))}
              onCommitOverride={(context, text, status) => commitOverride(r, context, text, status)} />)}
          </ul>
        )}
      </div>
    </aside>
      {dialogs.element}
    </>
  )
}

const STATUS_STYLE: Record<Row['localStatus'], { label: string; fg: string; bg: string }> = {
  missing: { label: 'Missing', fg: colors.textMuted, bg: colors.background },
  draft: { label: 'Draft', fg: colors.warning, bg: colors.warningLight },
  accepted: { label: 'Accepted', fg: colors.success, bg: colors.successLight },
  invalid: { label: 'Invalid', fg: colors.error, bg: colors.errorLight },
}

function StringRow({ row, canEdit, onCommit, onAccept, onCommitOverride }: {
  row: Row; canEdit: boolean; onCommit: (text: string) => void; onAccept: () => void
  onCommitOverride: (context: string, text: string, status?: TranslationStatus) => void
}) {
  const [text, setText] = useState(row.localText)
  const [showOccurrences, setShowOccurrences] = useState(row.overrides.size > 0)
  // Resync only when the stored text really changes. A plain effect also fires on mount, and when the mount's
  // effects flush late it overwrote what the user had already typed.
  useSyncedText(row.localText, setText)
  // Per-occurrence drafts arriving (Translate with AI) open the list, so their Draft badges are seen.
  useEffect(() => { if (row.overrides.size > 0) setShowOccurrences(true) }, [row.overrides.size])
  const s = STATUS_STYLE[row.localStatus]
  const typedInvalid = text !== '' && !isWellFormed(row.source, text)
  const contexts = row.contexts ?? []
  return (
    <li data-testid="translation-row" style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
        <div style={{ flex: 1, color: colors.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{row.source}</div>
        {row.occurrences > 1 && (
          <button type="button" aria-expanded={showOccurrences} aria-label={`${row.occurrences} occurrences of ${row.source}`}
            title="Occurs more than once. Expand to translate one occurrence differently."
            onClick={() => setShowOccurrences(v => !v)}
            style={{ flexShrink: 0, fontSize: 11, padding: '1px 6px', borderRadius: 999, border: `1px solid ${colors.border}`, background: 'none', color: colors.textSecondary, cursor: 'pointer' }}>
            ×{row.occurrences}{row.overrides.size ? ` · ${row.overrides.size} different` : ''}
          </button>
        )}
        <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 999, color: s.fg, background: s.bg }}>{s.label}</span>
      </div>
      <textarea
        aria-label={`Translation of ${row.source}`}
        value={text}
        disabled={!canEdit}
        rows={Math.min(4, Math.max(1, Math.ceil(row.source.length / 40)))}
        placeholder={row.inherited ? `Uses ${row.inherited}` : 'Not translated: renders in the default language'}
        onChange={e => setText(e.target.value)}
        onBlur={() => onCommit(text)}
        style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', border: `1px solid ${typedInvalid ? colors.error : colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, fontFamily: 'inherit', resize: 'vertical' }}
      />
      {typedInvalid && (
        <div style={{ color: colors.error, fontSize: 11 }}>Keep each {'{{variable}}'} and numbered tag from the source exactly once. Until fixed this string renders in the default language.</div>
      )}
      {row.localStatus === 'draft' && canEdit && (
        <button type="button" onClick={onAccept} style={{ marginTop: 4, padding: '2px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.textSecondary, fontSize: 11, cursor: 'pointer' }}>
          Accept
        </button>
      )}
      {showOccurrences && contexts.length > 1 && (
        <ul aria-label={`Occurrences of ${row.source}`} style={{ listStyle: 'none', margin: '6px 0 0', padding: '0 0 0 8px', borderLeft: `2px solid ${colors.border}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {contexts.map(c => (
            <OccurrenceItem key={c.context} label={c.label} source={row.source} shared={row.localText}
              override={row.overrides.get(c.context)} canEdit={canEdit}
              onCommit={(t, st) => onCommitOverride(c.context, t, st)} />
          ))}
        </ul>
      )}
    </li>
  )
}

/** One occurrence: uses the shared translation, or its own ("Translate this occurrence differently"). */
function OccurrenceItem({ label, source, shared, override, canEdit, onCommit }: {
  label: string; source: string; shared: string; override: TranslationOverride | undefined; canEdit: boolean
  onCommit: (text: string, status?: TranslationStatus) => void
}) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(override?.text ?? '')
  useSyncedText(override?.text ?? '', setText)
  const typedInvalid = text !== '' && !isWellFormed(source, text)
  const small: CSSProperties = { padding: '1px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.textSecondary, fontSize: 11, cursor: 'pointer' }
  const open = editing || !!override
  return (
    <li data-testid="occurrence-row" style={{ fontSize: fontSize.xs }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ flex: 1, color: colors.textSecondary }}>{label}</span>
        {override?.status === TranslationStatus.Draft && <span style={{ color: colors.warning, fontWeight: 600 }}>Draft</span>}
        {canEdit && !open && (
          <button type="button" onClick={() => { setText(shared); setEditing(true) }} style={small}>Translate this occurrence differently</button>
        )}
        {canEdit && override && (
          <button type="button" onClick={() => { setEditing(false); onCommit('') }} style={small}>Use shared translation</button>
        )}
      </div>
      {!open && <div style={{ color: colors.textMuted }}>{shared ? 'Uses the shared translation.' : 'Uses the shared translation (none yet).'}</div>}
      {open && (
        <>
          <textarea
            aria-label={`Translation of ${source} at ${label}`}
            autoFocus={editing && !override}
            value={text}
            disabled={!canEdit}
            rows={Math.min(3, Math.max(1, Math.ceil(source.length / 40)))}
            onChange={e => setText(e.target.value)}
            onBlur={() => {
              // Unchanged: nothing to store (an untouched copy of the shared text is not an override).
              if (override ? text === override.text : !text || text === shared) { if (!override) setEditing(false); return }
              onCommit(text)
              if (!text) setEditing(false)
            }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '3px 6px', border: `1px solid ${typedInvalid ? colors.error : colors.border}`, borderRadius: radius.sm, fontSize: fontSize.xs, fontFamily: 'inherit', resize: 'vertical' }}
          />
          {typedInvalid && <div style={{ color: colors.error, fontSize: 11 }}>Keep each {'{{variable}}'} and numbered tag from the source exactly once. Until fixed this occurrence uses the shared translation.</div>}
          {override?.status === TranslationStatus.Draft && canEdit && (
            <button type="button" onClick={() => onCommit(override.text, TranslationStatus.Accepted)} style={small}>Accept</button>
          )}
        </>
      )}
    </li>
  )
}

/** Copies `stored` into local edit state whenever it changes after mount — never on mount itself. */
function useSyncedText(stored: string, setText: (text: string) => void) {
  const last = useRef(stored)
  useEffect(() => {
    if (last.current === stored) return
    last.current = stored
    setText(stored)
  }, [stored, setText])
}
